import { useCurrency } from "@/lib/currency";
import { chargeDisclosure } from "@/lib/booking-display";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Crown, Loader2, MapPin, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MAYA_IMAGE_URL as mayaIcon } from "@/lib/maya-art";
import { getMayaPurchaseBySession } from "@/lib/maya-checkout.functions";
import { consumeMayaReturnPath } from "@/lib/maya-guest";
import { normalizeLang } from "@/i18n";

export const Route = createFileRoute("/maya/return")({
  head: () => ({
    meta: [
      { title: "Tu compra de Maya Premium · Mostrando México" },
      { name: "description", content: "Consulta el estado de tu compra y el acceso a tus Guías de Maya." },
      { property: "og:title", content: "Tu compra de Maya Premium · Mostrando México" },
      { property: "og:description", content: "Confirmación de compra y acceso a las Guías de Maya." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  validateSearch: (s: Record<string, unknown>) => ({
    session_id: typeof s.session_id === "string" ? s.session_id : undefined,
  }),
  component: MayaReturn,
});

type Lang = "es" | "en" | "fr" | "pt" | "it" | "de";

const L: Record<Lang, {
  processing: string;
  confirming: string;
  success: string;
  waiting: string;
  noSession: string;
  paymentConfirmed: string;
  processingBadge: string;
  productAcquired: string;
  passLine: string;
  amount: string; email: string; date: string; access: string; activated: string;
  emailNote: (email: string) => string;
  goToDestination: string;
  openMaya: string;
  goHome: string;
  waitDetail: string;
  timeoutHint: string;
  contactSupport: string;
  noSessionDetail: string;
  productPass: string;
  productDest: (d: string) => string;
  productGeneric: string;
}> = {
  de: {
    processing: "Zahlung wird verarbeitet…", confirming: "Dein Kauf wird bestätigt…", success: "Willkommen bei Maya Premium!", waiting: "Dein Kauf wird noch bestätigt…", noSession: "Kaufsitzung nicht gefunden", paymentConfirmed: "Zahlung bestätigt", processingBadge: "In Bearbeitung", productAcquired: "Erworbenes Produkt", passLine: "Dein Maya Pass schaltet alle verfügbaren Mexiko-Guides dauerhaft frei.", amount: "Betrag", email: "E-Mail", date: "Datum", access: "Zugang", activated: "Aktiviert", emailNote: (email) => `Eine Bestätigung wird an ${email} gesendet. Verwende dieselbe E-Mail, um deinen Zugang wiederherzustellen.`, goToDestination: "Zum freigeschalteten Reiseziel", openMaya: "Maya Premium öffnen", goHome: "Zur Startseite", waitDetail: "Wir warten auf die Zahlungsbestätigung von Stripe. Das dauert normalerweise einige Sekunden.", timeoutHint: "Stripe hat deine Zahlung bestätigt; dein Zugang wird in wenigen Minuten aktiviert. Lade die Seite neu oder komme später zurück.", contactSupport: "Kontakt aufnehmen", noSessionDetail: "Die Kaufkennung fehlt. Falls du bezahlt hast, öffne Maya Premium mit der beim Kauf verwendeten E-Mail.", productPass: "Maya Pass México", productDest: (destination) => `Maya Premium · ${destination}`, productGeneric: "Maya Premium",
  },
  es: {
    processing: "Procesando tu pago…",
    confirming: "Confirmando tu compra…",
    success: "¡Bienvenido a Maya Premium!",
    waiting: "Aún confirmando tu compra…",
    noSession: "No encontramos tu sesión",
    paymentConfirmed: "Pago confirmado",
    processingBadge: "Procesando",
    productAcquired: "Producto adquirido",
    passLine: "Tu Maya Pass desbloquea todas las guías de México de forma permanente.",
    amount: "Monto", email: "Correo", date: "Fecha", access: "Acceso", activated: "Activado",
    emailNote: (e) => `Te enviaremos una confirmación por correo a ${e}. Si alguna vez pierdes el acceso, vuelve al destino con este mismo correo y se reactivará automáticamente.`,
    goToDestination: "Ir al destino desbloqueado",
    openMaya: "Abrir Maya Premium",
    goHome: "Volver al inicio",
    waitDetail: "Estamos esperando la confirmación de Stripe. Esto suele tardar unos segundos.",
    timeoutHint: "Stripe confirmó tu pago; el acceso se activará en unos minutos. Puedes recargar esta página o volver más tarde.",
    contactSupport: "Contactar soporte",
    noSessionDetail: "No recibimos el identificador de tu compra. Si pagaste, abre Maya Premium con el correo que usaste y recuperaremos tu acceso.",
    productPass: "Maya Pass México",
    productDest: (d) => `Maya Premium · ${d}`,
    productGeneric: "Maya Premium",
  },
  en: {
    processing: "Processing your payment…",
    confirming: "Confirming your purchase…",
    success: "Welcome to Maya Premium!",
    waiting: "Still confirming your purchase…",
    noSession: "We couldn't find your session",
    paymentConfirmed: "Payment confirmed",
    processingBadge: "Processing",
    productAcquired: "Product acquired",
    passLine: "Your Maya Pass unlocks every Mexico guide permanently.",
    amount: "Amount", email: "Email", date: "Date", access: "Access", activated: "Activated",
    emailNote: (e) => `We'll email a confirmation to ${e}. If you ever lose access, return with the same email and it will unlock again automatically.`,
    goToDestination: "Go to the unlocked destination",
    openMaya: "Open Maya Premium",
    goHome: "Back to home",
    waitDetail: "We're waiting for Stripe to confirm. This usually takes a few seconds.",
    timeoutHint: "Stripe confirmed your payment; access will activate in a few minutes. You can reload this page or come back later.",
    contactSupport: "Contact support",
    noSessionDetail: "No purchase identifier was received. If you paid, open Maya Premium with your email and we'll recover your access.",
    productPass: "Maya Pass México",
    productDest: (d) => `Maya Premium · ${d}`,
    productGeneric: "Maya Premium",
  },
  fr: {
    processing: "Traitement du paiement…",
    confirming: "Confirmation de ton achat…",
    success: "Bienvenue chez Maya Premium !",
    waiting: "Toujours en cours de confirmation…",
    noSession: "Session introuvable",
    paymentConfirmed: "Paiement confirmé",
    processingBadge: "En cours",
    productAcquired: "Produit acquis",
    passLine: "Ton Maya Pass débloque toutes les guides du Mexique à vie.",
    amount: "Montant", email: "Email", date: "Date", access: "Accès", activated: "Activé",
    emailNote: (e) => `Nous t'enverrons une confirmation à ${e}. Si tu perds l'accès, reviens avec la même adresse et il sera réactivé automatiquement.`,
    goToDestination: "Aller à la destination débloquée",
    openMaya: "Ouvrir Maya Premium",
    goHome: "Retour à l'accueil",
    waitDetail: "Nous attendons la confirmation de Stripe. Cela prend généralement quelques secondes.",
    timeoutHint: "Stripe a confirmé le paiement ; l'accès s'activera dans quelques minutes. Tu peux recharger la page ou revenir plus tard.",
    contactSupport: "Contacter le support",
    noSessionDetail: "Nous n'avons pas reçu l'identifiant de l'achat. Si tu as payé, ouvre Maya Premium avec ton email et nous récupérerons l'accès.",
    productPass: "Maya Pass México",
    productDest: (d) => `Maya Premium · ${d}`,
    productGeneric: "Maya Premium",
  },
  pt: {
    processing: "Processando seu pagamento…",
    confirming: "Confirmando sua compra…",
    success: "Bem-vindo à Maya Premium!",
    waiting: "Ainda confirmando sua compra…",
    noSession: "Não encontramos sua sessão",
    paymentConfirmed: "Pagamento confirmado",
    processingBadge: "Processando",
    productAcquired: "Produto adquirido",
    passLine: "Seu Maya Pass desbloqueia todos os guias do México permanentemente.",
    amount: "Valor", email: "Email", date: "Data", access: "Acesso", activated: "Ativado",
    emailNote: (e) => `Enviaremos uma confirmação para ${e}. Se perder o acesso, volte com o mesmo email e ele será reativado automaticamente.`,
    goToDestination: "Ir para o destino desbloqueado",
    openMaya: "Abrir Maya Premium",
    goHome: "Voltar ao início",
    waitDetail: "Estamos aguardando a confirmação da Stripe. Isso costuma levar alguns segundos.",
    timeoutHint: "A Stripe confirmou seu pagamento; o acesso será ativado em alguns minutos. Recarregue a página ou volte mais tarde.",
    contactSupport: "Contatar suporte",
    noSessionDetail: "Não recebemos o identificador da compra. Se você pagou, abra a Maya Premium com seu email e recuperaremos o acesso.",
    productPass: "Maya Pass México",
    productDest: (d) => `Maya Premium · ${d}`,
    productGeneric: "Maya Premium",
  },
  it: {
    processing: "Elaborazione del pagamento…",
    confirming: "Confermando il tuo acquisto…",
    success: "Benvenuto in Maya Premium!",
    waiting: "Stiamo ancora confermando il tuo acquisto…",
    noSession: "Sessione non trovata",
    paymentConfirmed: "Pagamento confermato",
    processingBadge: "In elaborazione",
    productAcquired: "Prodotto acquistato",
    passLine: "Il tuo Maya Pass sblocca tutte le guide del Messico in modo permanente.",
    amount: "Importo", email: "Email", date: "Data", access: "Accesso", activated: "Attivato",
    emailNote: (e) => `Ti invieremo una conferma a ${e}. Se perdi l'accesso, torna con la stessa email e verrà riattivato automaticamente.`,
    goToDestination: "Vai alla destinazione sbloccata",
    openMaya: "Apri Maya Premium",
    goHome: "Torna alla home",
    waitDetail: "Stiamo aspettando la conferma di Stripe. Di solito ci vogliono pochi secondi.",
    timeoutHint: "Stripe ha confermato il pagamento; l'accesso si attiverà tra pochi minuti. Puoi ricaricare la pagina o tornare più tardi.",
    contactSupport: "Contatta il supporto",
    noSessionDetail: "Non abbiamo ricevuto l'identificatore dell'acquisto. Se hai pagato, apri Maya Premium con la tua email e recupereremo l'accesso.",
    productPass: "Maya Pass México",
    productDest: (d) => `Maya Premium · ${d}`,
    productGeneric: "Maya Premium",
  },
};

function pickLang(lang: string): Lang {
  const l = normalizeLang(lang);
  return (["es", "en", "fr", "pt", "it", "de"] as const).includes(l as Lang) ? (l as Lang) : "es";
}

function formatPrice(amount: number | null, currency: string | null): string {
  if (amount == null) return "";
  const cur = (currency ?? "usd").toUpperCase();
  return `${(amount / 100).toFixed(2)} ${cur}`;
}

const MAX_POLLS = 20;

function MayaReturn() {
  useCurrency(); // subscribe: formatPrice reads the currency store
  const { session_id } = Route.useSearch();
  const { i18n } = useTranslation();
  const t = L[pickLang(i18n.language)];
  const { currency: displayCurrency, format, rates } = useCurrency();
  const formatPrice = (amount: number | null, currency: string | null) => amount == null ? "" : `${format(amount / 100, currency, { locale: i18n.language, decimals: 2 })} · ${chargeDisclosure(amount / 100, currency ?? "USD", displayCurrency, i18n.language, rates)}`;
  const qc = useQueryClient();
  const [pollCount, setPollCount] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ["maya-purchase", session_id],
    queryFn: async () => {
      setPollCount((c) => c + 1);
      return getMayaPurchaseBySession({ data: { sessionId: session_id ?? "" } });
    },
    enabled: !!session_id,
    refetchInterval: (q) => {
      if (q.state.data?.found) return false;
      if (pollCount >= MAX_POLLS) return false;
      return 3000;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
  });

  const [returnPath] = useState<string | null>(() => consumeMayaReturnPath());
  const timedOut = !data?.found && pollCount >= MAX_POLLS;

  useEffect(() => {
    if (data?.found) qc.invalidateQueries({ queryKey: ["maya-access"] });
  }, [data?.found, qc]);

  const isPass = data?.product === "maya_pass_one_time";
  const destination = data?.destinationKey ?? null;
  const productLabel = isPass
    ? t.productPass
    : destination
      ? t.productDest(destination)
      : t.productGeneric;

  const heading = useMemo(() => {
    if (!session_id) return t.noSession;
    if (isLoading || !data) return t.confirming;
    if (!data.found) return t.waiting;
    return t.success;
  }, [session_id, isLoading, data, t]);

  return (
    <>
    <SiteHeader />
    <div className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center px-6 py-10 text-center">
      <div className="mb-4 grid h-16 w-16 place-items-center rounded-2xl bg-card shadow ring-2 ring-[color:var(--gold)]">
        <img loading="lazy" decoding="async" src={mayaIcon} alt="Maya" className="h-14 w-14 rounded-xl object-contain" />
      </div>

      {data?.found ? (
        <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-700">
          <Check className="h-3.5 w-3.5" /> {t.paymentConfirmed}
        </div>
      ) : session_id ? (
        <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-700">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t.processingBadge}
        </div>
      ) : null}

      <h1 className="font-display text-2xl font-semibold">{heading}</h1>

      {data?.found ? (
        <>
          <div className="mt-5 w-full rounded-2xl border border-border bg-card p-5 text-left">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
              {isPass ? <Crown className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
              {t.productAcquired}
            </div>
            <p className="mt-1 text-base font-semibold">{productLabel}</p>

            {destination && !isPass && (
              <div className="mt-3 flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-primary" />
                <span className="font-medium capitalize">{destination}</span>
              </div>
            )}
            {isPass && (
              <p className="mt-2 text-sm text-muted-foreground">{t.passLine}</p>
            )}

            <dl className="mt-4 grid grid-cols-2 gap-2 text-xs">
              {data.amount != null && (
                <>
                  <dt className="text-muted-foreground">{t.amount}</dt>
                  <dd className="text-right font-semibold">{formatPrice(data.amount, data.currency)}</dd>
                </>
              )}
              {data.customerEmailMasked && (
                <>
                  <dt className="text-muted-foreground">{t.email}</dt>
                  <dd className="truncate text-right">{data.customerEmailMasked}</dd>
                </>
              )}
              {data.createdAt && (
                <>
                  <dt className="text-muted-foreground">{t.date}</dt>
                  <dd className="text-right">{new Date(data.createdAt).toLocaleString()}</dd>
                </>
              )}
              <dt className="text-muted-foreground">{t.access}</dt>
              <dd className="text-right font-semibold text-emerald-700">{t.activated}</dd>
            </dl>

            <p className="mt-4 rounded-lg bg-muted/50 p-3 text-[11px] leading-relaxed text-muted-foreground">
              {t.emailNote(data.customerEmailMasked ?? "…")}
            </p>
          </div>

          <div className="mt-6 flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
            {returnPath && !isPass && (
              <Button asChild>
                <Link to={returnPath}>
                  {t.goToDestination} <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            )}
            <Button asChild variant={returnPath && !isPass ? "outline" : "default"}>
              <Link to="/maya">{t.openMaya}</Link>
            </Button>
            <Button asChild variant="ghost">
              <Link to="/">{t.goHome}</Link>
            </Button>
          </div>
        </>
      ) : session_id ? (
        <>
          <p className="mt-2 max-w-md text-sm text-muted-foreground">
            {timedOut ? t.timeoutHint : t.waitDetail}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Button asChild variant="outline">
              <Link to="/maya">{t.openMaya}</Link>
            </Button>
            {timedOut && (
              <Button asChild variant="ghost">
                <Link to="/contact">{t.contactSupport}</Link>
              </Button>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-muted-foreground">{t.noSessionDetail}</p>
          <div className="mt-6">
            <Button asChild>
              <Link to="/maya">{t.openMaya}</Link>
            </Button>
          </div>
        </>
      )}
    </div>
    <SiteFooter />
    </>
  );
}
